/**
 * i18n bootstrap.
 *
 * Detection priority:
 *   1. User profile preference (handled by Layout after login; calls setLanguage)
 *   2. localStorage (handled by i18next-browser-languagedetector)
 *   3. Browser navigator language
 *   4. Fallback: English
 *
 * SUPPORTED MODES
 *   'en'   — English UI, LTR
 *   'ar'   — Arabic UI, RTL
 *   'both' — Bilingual UI; renders "EN / AR" inline for every translation key.
 *            Direction stays LTR so the slash separator reads naturally.
 *
 * NUMERAL RULE (user mandate):
 *   All numbers — currency, quantities, dates — MUST render in Latin digits
 *   (0–9) regardless of the active language. Handled in `utils/format.js`
 *   by pinning every Intl formatter to `numberingSystem: 'latn'`.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en/translation.json';
import ar from './locales/ar/translation.json';
import { attachRuntimeGuard } from './i18n/runtimeGuard';

/**
 * "bilingual" post-processor — when the user picked the "Both" mode we
 * fetch the EN value and append the AR value separated by a divider.
 *
 * Activation is gated by `localStorage.app_lang === 'both'`; i18next's
 * active language stays 'en' so RTL/LTR stays predictable.
 *
 * Registered BEFORE .init() so i18next wires it into the resolve chain
 * up front (registering after init is silently dropped in v23+).
 */
const bilingualPostProcessor = {
  type: 'postProcessor',
  name: 'bilingual',
  process(value, key) {
    let mode = 'en';
    try { mode = localStorage.getItem('app_lang') || 'en'; } catch { /* ignore */ }
    if (mode !== 'both') return value;
    // `key` may be an array when i18next resolved a fallback chain — use the first.
    const k = Array.isArray(key) ? key[0] : key;
    const arVal = i18n.getResource('ar', 'translation', k);
    if (!arVal || arVal === value) return value;
    // Avoid double-joining when a value already contains the separator
    if (typeof value === 'string' && value.includes(' / ')) return value;
    return `${value} / ${arVal}`;
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(bilingualPostProcessor)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    fallbackLng: 'en',
    supportedLngs: ['en', 'ar'],
    interpolation: { escapeValue: false },
    saveMissing: true,
    load: 'languageOnly',  // 'ar-SA' → 'ar'
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'app_lang',
      // Do NOT cache i18next's active language to localStorage — we own
      // the 'app_lang' key ourselves (it can be 'both' which i18next
      // doesn't understand). Caching would clobber our mode token with
      // 'en' or 'ar' on every re-render.
      caches: [],
    },
    postProcess: ['bilingual'],
  });

// P11.7 — runtime safety: log missing keys + visible "[missing:key]" markers.
attachRuntimeGuard(i18n);

/** Map any language code into one of our supported codes. */
const normalize = (code) => {
  if (!code) return 'en';
  const base = code.toLowerCase().split('-')[0];
  if (base === 'ar') return 'ar';
  if (base === 'both') return 'both';
  return 'en';
};

/**
 * Set <html lang> + dir + persist + i18next state.
 *
 * For 'both' mode i18next stays on 'en' (the post-processor handles the
 * AR concatenation) but localStorage stores 'both' so the toggle UI and
 * the post-processor can read the user's actual choice.
 */
export const applyLanguage = (raw) => {
  const mode = normalize(raw);
  const activeLang = mode === 'ar' ? 'ar' : 'en';
  const dir = mode === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('lang', activeLang);
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('data-lang-mode', mode);
  i18n.changeLanguage(activeLang);
  try { localStorage.setItem('app_lang', mode); } catch { /* ignore */ }
  // Notify any subscribed components (e.g. LanguageToggle) of the change
  // even when i18next's own listener didn't fire (mode flipped en↔both).
  try {
    window.dispatchEvent(new CustomEvent('app-lang-changed', { detail: mode }));
  } catch { /* ignore */ }
  return mode;
};

/** Current bilingual mode token: 'en' | 'ar' | 'both'. */
export const getLanguageMode = () => {
  try {
    const stored = localStorage.getItem('app_lang');
    if (stored === 'ar' || stored === 'en' || stored === 'both') return stored;
  } catch { /* ignore */ }
  return (i18n.language || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
};

// Apply on initial load so <html> dir is correct before first paint.
applyLanguage(getLanguageMode());

export default i18n;
